/**
 * Models for Application Questions endpoints.
 * Keys remain snake_case to align with backend.
 */

/** Enum for the type of question. */
export enum QuestionType {
  SHORT_ANSWER = 'SHORT_ANSWER',
  PARAGRAPH = 'PARAGRAPH',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  DROPDOWN = 'DROPDOWN',
  MULTIPLE_CHOICE_GRID = 'MULTIPLE_CHOICE_GRID',
  SINGLE_CHOICE_GRID = 'SINGLE_CHOICE_GRID',
  SECTION = 'SECTION',
}

/** Enum for the applicant role type. */
export enum ApplicantRoleType {
  MENTOR = 'MENTOR',
  MENTEE = 'MENTEE',
  BOTH = 'BOTH',
}

/** Enum for the matching importance of a question. */
export enum MatchingImportance {
  NOT_USED = 'NOT_USED',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

/** DTO for creating a new question. */
export interface QuestionCreateDTO {
  question_text: string;
  question_type: QuestionType;
  for_role: ApplicantRoleType;
  required: boolean;
  options?: object;
  matching_importance?: MatchingImportance;
  matching_question_id?: string;
}

/** DTO for updating an existing question. */
export interface QuestionUpdateDTO {
  question_text?: string;
  question_type?: QuestionType;
  for_role?: ApplicantRoleType;
  required?: boolean;
  options?: object;
  matching_importance?: MatchingImportance;
  matching_question_id?: string;
}

/** DTO for question responses. */
export interface QuestionDTO {
  id: string;
  application_id: string;
  question_text: string;
  question_type: QuestionType;
  for_role: ApplicantRoleType;
  required: boolean;
  options?: object;
  matching_importance?: MatchingImportance;
  matching_question_id?: string;
  allow_other?: boolean;
  other_label?: string;
  placeholder?: string;
  one_response_per_row?: boolean;
}

/** Options structure for standard choice questions (single/multiple choice, dropdown). */
export interface StandardQuestionOptions {
  /** Key-value pairs of option values */
  options: Record<string, string>;
}

/** Options structure for grid questions. */
export interface GridQuestionOptions {
  /** Row labels */
  rows: string[];
  /** Column labels */
  columns: string[];
}



/** DTO for updating question order. */
export interface QuestionOrderUpdateDTO {
  question_ids: string[];
}

export interface ErrorResponse {
  detail: string;
}

// Legacy interfaces for backward compatibility during transition
export interface Question extends QuestionDTO {}
export interface QuestionCreate extends QuestionCreateDTO {}
export interface QuestionUpdate extends QuestionUpdateDTO {}
export interface QuestionOrderUpdate extends QuestionOrderUpdateDTO {}
